/**
 * Slice 3a — blank-page separator auto-split.
 *
 * Pure conversion of a per-page blank/not-blank vector into document page
 * ranges, using the sheet-parity rule:
 *   - simplex: every blank page is a separator;
 *   - duplex:  only a blank at an ODD (1-based) index is a separator (the front
 *     of a physical sheet). A blank at an EVEN index is the empty back of an
 *     odd-length document (filler) and never splits.
 * Pages are 1-based; ranges are inclusive. The result feeds the worker's
 * existing buildGroupsFromBoundaries.
 */
/** Default blank-detection brightness threshold (0–1). Shared across worker + UI. */
export const DEFAULT_SEPARATION_BRIGHTNESS_THRESHOLD = 0.92;
/** Build the default separation config (UI presets / programmatic use). */
export function makeDefaultSeparationConfig(threshold = DEFAULT_SEPARATION_BRIGHTNESS_THRESHOLD) {
    return { enabled: true, mode: "blank", brightnessThreshold: threshold, keepSeparatorPages: false };
}
/** Runtime shape guard for untrusted input (route bodies). */
export function isValidSeparationConfig(v) {
    if (typeof v !== "object" || v === null)
        return false;
    const o = v;
    const baseOk = typeof o["enabled"] === "boolean" &&
        typeof o["brightnessThreshold"] === "number" &&
        o["brightnessThreshold"] >= 0 && o["brightnessThreshold"] <= 1 &&
        typeof o["keepSeparatorPages"] === "boolean";
    if (!baseOk)
        return false;
    if (o["mode"] === "blank")
        return true;
    if (o["mode"] === "barcode") {
        const value = o["separatorBarcodeValue"];
        const hasValue = typeof value === "string" && value.trim().length > 0;
        const hasPatch = o["separatorPatchCode"] === true;
        // optional fields, when present, must be the right type
        if (value !== undefined && typeof value !== "string")
            return false;
        if (o["separatorPatchCode"] !== undefined && typeof o["separatorPatchCode"] !== "boolean")
            return false;
        return hasValue || hasPatch; // barcode mode needs at least one trigger
    }
    return false;
}
/**
 * Slice 3b — known patch-code decoded values. The OCR endpoint has no native
 * AIIM patch-pattern decoder, so a patch separator sheet carries a known
 * Code-39/128/QR value; these are the default identifiers a deployment prints.
 * Operators configure the exact value via separatorBarcodeValue when theirs differ.
 */
export const PATCH_CODE_VALUES = new Set([
    "SEPARATOR", "PATCH1", "PATCH2", "PATCH3", "PATCH4", "PATCH5", "PATCH6", "PATCHT",
]);
/**
 * Slice 3b — pure separator policy for barcode mode. A page is a separator when
 * one of its decoded barcode values equals the configured value, or (when patch
 * is enabled) is a known patch-code value. Generic OCR output in → boolean out.
 */
export function isBarcodeSeparator(decodedValues, cfg) {
    const wanted = cfg.separatorBarcodeValue?.trim();
    for (const raw of decodedValues) {
        const v = raw.trim();
        if (v.length === 0)
            continue;
        if (wanted && v === wanted)
            return true;
        if (cfg.separatorPatchCode && PATCH_CODE_VALUES.has(v))
            return true;
    }
    return false;
}
export function computeSeparatorBoundaries(blankFlags, opts) {
    return opts.duplex
        ? duplexBoundaries(blankFlags, opts.keepSeparatorPages)
        : simplexBoundaries(blankFlags, opts.keepSeparatorPages);
}
function simplexBoundaries(blankFlags, keep) {
    const out = [];
    let docStart = null;
    for (let i = 1; i <= blankFlags.length; i++) {
        if (blankFlags[i - 1]) {
            if (docStart !== null) {
                out.push({ startPage: docStart, endPage: keep ? i : i - 1 });
                docStart = null;
            }
        }
        else if (docStart === null) {
            docStart = i;
        }
    }
    if (docStart !== null)
        out.push({ startPage: docStart, endPage: blankFlags.length });
    return out;
}
function duplexBoundaries(blankFlags, keep) {
    const out = [];
    let docStart = null;
    let lastContent = null;
    for (let i = 1; i <= blankFlags.length; i++) {
        const blank = blankFlags[i - 1];
        if (blank && i % 2 === 1) {
            // separator sheet front (odd index)
            if (docStart !== null) {
                out.push({ startPage: docStart, endPage: keep ? i - 1 : lastContent });
                docStart = null;
                lastContent = null;
            }
            // the separator's even back (if blank) is ignored below since docStart is null
        }
        else if (!blank) {
            if (docStart === null)
                docStart = i;
            lastContent = i;
        }
        // even-index blank with an open doc = trailing filler → ignored (trimmed)
    }
    if (docStart !== null) {
        out.push({ startPage: docStart, endPage: keep ? blankFlags.length : lastContent });
    }
    return out;
}
//# sourceMappingURL=separator-boundaries.js.map